import React, { useEffect, useState } from 'react';
import Navbar from '../common/Navbar';
import { 
  Search, 
  Filter, 
  RefreshCw,
  X,
  ChevronLeft,
  ChevronRight,
  FolderOpen,
  Archive as ArchiveIcon,
  Calendar,
  DollarSign,
  Edit,
  Save
} from 'lucide-react';
import { projectService, leadService } from '../../services/api';

// Edit Project Modal Component
function EditProjectModal({ project, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    estimated_revenue: project.estimated_revenue || 0,
    project_name: project.project_name || '',
    next_followup_date: project.next_followup_date ? project.next_followup_date.split('T')[0] : '',
    notes: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const newErrors = {};
    if (formData.estimated_revenue < 0) {
      newErrors.estimated_revenue = 'Revenue cannot be negative';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    try {
      setSubmitting(true);
      await leadService.update(project.lead_id, {
        estimated_revenue: parseFloat(formData.estimated_revenue) || 0,
        project_name: formData.project_name,
        next_followup_date: formData.next_followup_date || null,
        notes: formData.notes || null
      });
      alert('Project updated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error updating project:', error);
      alert(error.response?.data?.message || 'Failed to update project');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <Edit className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Edit Project</h2>
              <p className="text-slate-400 text-sm">{project.organisation_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Project Name
            </label>
            <input
              type="text"
              value={formData.project_name}
              onChange={(e) => setFormData({...formData, project_name: e.target.value})}
              placeholder="Enter project name..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Estimated Revenue
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="number"
                value={formData.estimated_revenue}
                onChange={(e) => {
                  setFormData({...formData, estimated_revenue: e.target.value});
                  if (errors.estimated_revenue) setErrors({...errors, estimated_revenue: ''});
                }}
                placeholder="0"
                step="1000"
                min="0"
                className={`w-full pl-11 pr-4 py-3 bg-slate-800 border ${
                  errors.estimated_revenue ? 'border-red-500' : 'border-slate-700'
                } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all`}
              />
            </div>
            {errors.estimated_revenue && (
              <p className="mt-2 text-sm text-red-400">⚠️ {errors.estimated_revenue}</p>
            )}
            <p className="mt-2 text-sm text-slate-500">
              Current: {formatCurrency(project.estimated_revenue || 0)}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Next Follow-Up Date
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <input
                type="date"
                value={formData.next_followup_date}
                onChange={(e) => setFormData({...formData, next_followup_date: e.target.value})}
                className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Update Notes <span className="text-slate-500">(Optional)</span>
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Add notes about this update..."
              rows={3}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all resize-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {submitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Archive Modal Component (inline to avoid import issues)
function ArchiveModal({ project, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    client_feedback: '',
    evaluation_rating: '',
    project_performance: ''
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const ratings = [
    { value: 'excellent', label: 'Excellent', color: 'text-green-400', stars: 5 },
    { value: 'good', label: 'Good', color: 'text-blue-400', stars: 4 },
    { value: 'fair', label: 'Fair', color: 'text-yellow-400', stars: 3 },
    { value: 'poor', label: 'Poor', color: 'text-orange-400', stars: 2 },
    { value: 'disaster', label: 'Disaster', color: 'text-red-400', stars: 1 }
  ];

  const validate = () => {
    const newErrors = {};
    if (!formData.client_feedback.trim()) newErrors.client_feedback = 'Client feedback is required';
    if (!formData.evaluation_rating) newErrors.evaluation_rating = 'Evaluation rating is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    try {
      setSubmitting(true);
      await projectService.archive(project.lead_id, formData);
      alert('Project archived successfully!');
      onSuccess();
    } catch (error) {
      console.error('Error archiving project:', error);
      alert(error.response?.data?.message || 'Failed to archive project');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-slate-900 rounded-2xl border border-slate-800 p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
              <ArchiveIcon className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Archive Project</h2>
              <p className="text-slate-400 text-sm">{project.organisation_name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Client Feedback <span className="text-red-400">*</span>
            </label>
            <textarea
              value={formData.client_feedback}
              onChange={(e) => {
                setFormData({...formData, client_feedback: e.target.value});
                if (errors.client_feedback) setErrors({...errors, client_feedback: ''});
              }}
              placeholder="Describe the client's feedback on the project..."
              rows={4}
              className={`w-full px-4 py-3 bg-slate-800 border ${
                errors.client_feedback ? 'border-red-500' : 'border-slate-700'
              } rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all resize-none`}
            />
            {errors.client_feedback && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                ⚠️ {errors.client_feedback}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">
              Evaluation Rating <span className="text-red-400">*</span>
            </label>
            <div className="space-y-3">
              {ratings.map((rating) => (
                <label
                  key={rating.value}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.evaluation_rating === rating.value
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-slate-700 bg-slate-800 hover:border-slate-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="evaluation_rating"
                    value={rating.value}
                    checked={formData.evaluation_rating === rating.value}
                    onChange={(e) => {
                      setFormData({...formData, evaluation_rating: e.target.value});
                      if (errors.evaluation_rating) setErrors({...errors, evaluation_rating: ''});
                    }}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className={`font-semibold ${rating.color}`}>{rating.label}</span>
                </label>
              ))}
            </div>
            {errors.evaluation_rating && (
              <p className="mt-2 text-sm text-red-400">⚠️ {errors.evaluation_rating}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Project Performance Notes <span className="text-slate-500">(Optional)</span>
            </label>
            <textarea
              value={formData.project_performance}
              onChange={(e) => setFormData({...formData, project_performance: e.target.value})}
              placeholder="Additional notes..."
              rows={3}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all resize-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white rounded-lg font-semibold transition-all shadow-lg disabled:opacity-50"
            >
              {submitting ? 'Archiving...' : 'Archive Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  
  const [filters, setFilters] = useState({
    consultant_id: '',
    sector_id: '',
    country_id: ''
  });
  
  const [lookups, setLookups] = useState({
    consultants: [],
    sectors: [],
    countries: []
  });

  useEffect(() => {
    loadLookups();
    loadProjects();
  }, [pagination.page]);

  const loadLookups = async () => {
    try {
      const response = await leadService.getLookups();
      const data = response?.data?.data || response?.data || {};
      
      setLookups({
        consultants: data.consultants || [],
        sectors: data.sectors || [],
        countries: data.countries || []
      });
    } catch (error) {
      console.error('Error loading lookups:', error);
      setLookups({
        consultants: [],
        sectors: [],
        countries: []
      });
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await projectService.getActiveProjects({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...filters
      });
      
      setProjects(response.data.data.projects);
      setPagination(prev => ({
        ...prev,
        total: response.data.data.pagination.total,
        pages: response.data.data.pagination.pages
      }));
    } catch (error) {
      console.error('Error loading projects:', error);
      alert('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    loadProjects();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadProjects();
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({
      consultant_id: '',
      sector_id: '',
      country_id: ''
    });
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => loadProjects(), 100);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadProjects();
  };

  const handleEditClick = (project) => {
    setSelectedProject(project);
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    setSelectedProject(null);
    loadProjects();
  };

  const handleArchiveClick = (project) => {
    setSelectedProject(project);
    setShowArchiveModal(true);
  };

  const handleArchiveSuccess = () => {
    setShowArchiveModal(false);
    setSelectedProject(null);
    loadProjects();
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length;

  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-400 text-lg">Loading projects...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <Navbar />
      
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Active Projects</h1>
            <p className="text-slate-400">
              {pagination.total} projects in execution
              {activeFilterCount > 0 && ` • ${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} active`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-slate-900 rounded-xl p-4 mb-6 border border-slate-800">
          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search projects..."
                  className="w-full pl-11 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                />
              </div>
            </form>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${
                showFilters || activeFilterCount > 0
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              <Filter className="w-5 h-5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="bg-white text-blue-600 text-xs font-bold px-2 py-0.5 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>

            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold transition-colors"
            >
              Search
            </button>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-800">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Consultant</label>
                  <select
                    name="consultant_id"
                    value={filters.consultant_id}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Consultants</option>
                    {lookups.consultants.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Sector</label>
                  <select
                    name="sector_id"
                    value={filters.sector_id}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Sectors</option>
                    {lookups.sectors.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Country</label>
                  <select
                    name="country_id"
                    value={filters.country_id}
                    onChange={handleFilterChange}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Countries</option>
                    {lookups.countries.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-4">
                <button
                  onClick={handleApplyFilters}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold text-sm transition-colors"
                >
                  Apply Filters
                </button>
                <button
                  onClick={handleClearFilters}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors"
                >
                  <X className="w-4 h-4" />
                  Clear All
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Projects Table */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800 border-b border-slate-700">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Project</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Client</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Consultant</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Sector</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Days Active</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-300">Revenue</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-300">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {projects.length > 0 ? (
                  projects.map((project) => (
                    <tr key={project.lead_id} className="hover:bg-slate-800/50 transition-colors group">
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-white font-medium">{project.organisation_name}</p>
                          {project.project_name && (
                            <p className="text-slate-500 text-sm">{project.project_name}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-slate-300">{project.client_name || 'N/A'}</p>
                          {project.client_email && (
                            <p className="text-slate-500 text-xs">{project.client_email}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-slate-300">{project.consultant_name || 'Unassigned'}</td>
                      <td className="px-6 py-4 text-slate-300">{project.sector_name || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-blue-400">
                          <Calendar className="w-4 h-4" />
                          <span>{project.days_active || 0} days</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                          <DollarSign className="w-4 h-4" />
                          {formatCurrency(project.estimated_revenue || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleEditClick(project)}
                            className="px-3 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-colors flex items-center gap-2"
                            title="Edit project"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleArchiveClick(project)}
                            className="px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg transition-colors flex items-center gap-2"
                            title="Archive project"
                          >
                            <ArchiveIcon className="w-4 h-4" />
                            Archive
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-16">
                      <div className="text-center">
                        <FolderOpen className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                        <p className="text-slate-400 text-lg mb-2">No active projects</p>
                        <p className="text-slate-500 text-sm">
                          Projects with "Project execution" status will appear here
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="bg-slate-800 px-6 py-4 flex items-center justify-between border-t border-slate-700">
              <div className="text-sm text-slate-400">
                Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} projects
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(pagination.pages, 5))].map((_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                          pagination.page === page
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedProject && (
        <EditProjectModal
          project={selectedProject}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProject(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}

      {/* Archive Modal */}
      {showArchiveModal && selectedProject && (
        <ArchiveModal
          project={selectedProject}
          onClose={() => {
            setShowArchiveModal(false);
            setSelectedProject(null);
          }}
          onSuccess={handleArchiveSuccess}
        />
      )}
    </div>
  );
}